import { useCallback, useMemo, useRef, useState } from 'react';
import { ArtistPortalArtistRef } from 'fym-shared/src/smartlinks/artistPortals';
import {
  useBeginArtistPortalAuthMutation,
  useCompleteArtistPortalAuthMutation,
  useDisconnectArtistPortalMutation,
  useGetArtistPortalArtistsMutation,
  useSelectArtistPortalArtistMutation,
  useSyncArtistPortalMutation,
} from '../../../redux/api/fymAPI';
import { ServiceConnection } from '../../../redux/api/fymAPI.types';
import { trackEvent } from '../../analytics/sml/analytics';
import { ErrorAPIResult, isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { ArtistPortalAuthCancelledError } from './artistPortalAuth/ArtistPortalAuthCancelledError';
import { openSpotifyAuth } from './artistPortalAuth/openSpotifyAuth';
import { portalConnectReturnTo } from './artistPortalAuth/portalConnectReturnTo';
import { rememberPortalReturn } from './portalReturn/portalReturnSession';

interface UseArtistPortalConnectProps {
  accountId: number;
}

export interface ArtistPortalConnectResult {
  connection: ServiceConnection;
  /** More than one entry means the artist must still pick. */
  artists: ArtistPortalArtistRef[];
}

/**
 * The five things an artist can do to a portal connection.
 *
 * Every call answers with the result rather than throwing, and keeps the last
 * server error for the card or the form to show. A cancelled sign-in answers
 * `null` and leaves no error: closing the window is an answer, not a fault.
 */
export function useArtistPortalConnect({ accountId }: UseArtistPortalConnectProps) {
  const [beginAuth, { isLoading: isBeginning }] = useBeginArtistPortalAuthMutation();
  const [completeAuth, { isLoading: isCompleting }] = useCompleteArtistPortalAuthMutation();
  const [selectArtistPortalArtist, { isLoading: isSelecting }] = useSelectArtistPortalArtistMutation();
  const [syncArtistPortal, { isLoading: isSyncing }] = useSyncArtistPortalMutation();
  const [disconnectArtistPortal, { isLoading: isDisconnecting }] = useDisconnectArtistPortalMutation();
  const [getArtists, { isLoading: isLoadingArtists }] = useGetArtistPortalArtistsMutation();

  const [isSigningIn, setSigningIn] = useState(false);
  const [error, setError] = useState<ErrorAPIResult['error']>();
  /*
   * One sign-in at a time. A second press would start a second PKCE session
   * and leave the first one's state stranded in Redis, so it answers null.
   * A ref, not state: the guard must hold within one render.
   */
  const isConnectingRef = useRef(false);

  const complete = useCallback(
    async (
      integrationType: string,
      body: Parameters<typeof completeAuth>[0]['body']
    ): Promise<ArtistPortalConnectResult | null> => {
      const result = await completeAuth({ accountId, integrationType, body });
      if (!isSuccessfulResult(result)) {
        setError(result.error);
        return null;
      }
      return result.data;
    },
    [accountId, completeAuth]
  );

  const connectSpotify = useCallback(
    async (integrationType: string): Promise<ArtistPortalConnectResult | null> => {
      if (isConnectingRef.current) {
        return null;
      }
      isConnectingRef.current = true;
      setError(undefined);
      trackEvent('portal_connect_started', { service: integrationType });
      try {
        const begun = await beginAuth({ accountId, integrationType });
        if (!isSuccessfulResult(begun)) {
          setError(begun.error);
          return null;
        }

        setSigningIn(true);
        let redirectUrl: string;
        try {
          const opened = await openSpotifyAuth({
            authUrl: begun.data.authUrl,
            redirectUrlPrefix: begun.data.redirectUrlPrefix,
          });
          redirectUrl = opened.redirectUrl;
        } catch (signInError) {
          if (!(signInError instanceof ArtistPortalAuthCancelledError)) {
            setError(signInError as Error);
          }
          return null;
        } finally {
          setSigningIn(false);
        }

        return await complete(integrationType, { sessionUuid: begun.data.sessionUuid, redirectUrl });
      } finally {
        isConnectingRef.current = false;
      }
    },
    [accountId, beginAuth, complete]
  );

  /**
   * A sign-in that returns through music24-api (`oauthCallback`, YouTube).
   *
   * The page leaves for the provider and comes back to the Catalog screen, which
   * reads the outcome (`usePortalConnectReturn`). So this answers only when it
   * could not start: false, with the error kept.
   */
  const connectWithCallback = useCallback(
    async (integrationType: string): Promise<boolean> => {
      if (isConnectingRef.current) {
        return false;
      }
      isConnectingRef.current = true;
      setError(undefined);
      trackEvent('portal_connect_started', { service: integrationType });
      try {
        const begun = await beginAuth({ accountId, integrationType, returnTo: portalConnectReturnTo(accountId) });
        if (!isSuccessfulResult(begun)) {
          setError(begun.error);
          return false;
        }
        setSigningIn(true);
        window.location.assign(begun.data.authUrl);
        return true;
      } finally {
        isConnectingRef.current = false;
      }
    },
    [accountId, beginAuth]
  );

  /**
   * A sign-in that MusicAPI runs and that comes back to the generic return page (Audiomack,
   * `portalReturn/`). The page leaves for the consent page; the return page finishes the
   * connection. So this answers only when it could not start: false, with the error kept.
   */
  const connectWithReturnPage = useCallback(
    async (integrationType: string): Promise<boolean> => {
      if (isConnectingRef.current) {
        return false;
      }
      isConnectingRef.current = true;
      setError(undefined);
      trackEvent('portal_connect_started', { service: integrationType });
      try {
        const begun = await beginAuth({ accountId, integrationType });
        if (!isSuccessfulResult(begun)) {
          setError(begun.error);
          return false;
        }
        rememberPortalReturn(begun.data.sessionUuid, accountId);
        setSigningIn(true);
        window.location.assign(begun.data.authUrl);
        return true;
      } finally {
        isConnectingRef.current = false;
      }
    },
    [accountId, beginAuth]
  );

  /** The artists the stored sign-in holds, for the picker after a callback sign-in. */
  const loadArtists = useCallback(
    async (integrationType: string): Promise<ArtistPortalArtistRef[] | null> => {
      setError(undefined);
      const result = await getArtists({ accountId, integrationType });
      if (!isSuccessfulResult(result)) {
        setError(result.error);
        return null;
      }
      return result.data.artists;
    },
    [accountId, getArtists]
  );

  const connectDeezer = useCallback(
    async (integrationType: string, email: string, password: string): Promise<ArtistPortalConnectResult | null> => {
      setError(undefined);
      return complete(integrationType, { email, password });
    },
    [complete]
  );

  const selectArtist = useCallback(
    async (integrationType: string, externalArtistId: string): Promise<boolean> => {
      setError(undefined);
      const result = await selectArtistPortalArtist({ accountId, integrationType, externalArtistId });
      if (!isSuccessfulResult(result)) {
        setError(result.error);
        return false;
      }
      return true;
    },
    [accountId, selectArtistPortalArtist]
  );

  const sync = useCallback(
    async (integrationType: string): Promise<boolean> => {
      setError(undefined);
      const result = await syncArtistPortal({ accountId, integrationType });
      if (!isSuccessfulResult(result)) {
        setError(result.error);
        return false;
      }
      return true;
    },
    [accountId, syncArtistPortal]
  );

  const disconnect = useCallback(
    async (integrationType: string): Promise<boolean> => {
      setError(undefined);
      const result = await disconnectArtistPortal({ accountId, integrationType });
      if (!isSuccessfulResult(result)) {
        setError(result.error);
        return false;
      }
      return true;
    },
    [accountId, disconnectArtistPortal]
  );

  /** Drops the last error, so a sheet opened for another service starts clean. */
  const clearError = useCallback(() => setError(undefined), []);

  const isBusy =
    isBeginning || isSigningIn || isCompleting || isSelecting || isSyncing || isDisconnecting || isLoadingArtists;

  // Memoised, so a `TypedMemo` child does not re-render on every parent render.
  return useMemo(
    () => ({
      connectSpotify,
      connectWithCallback,
      connectWithReturnPage,
      connectDeezer,
      loadArtists,
      selectArtist,
      sync,
      disconnect,
      isBusy,
      error,
      clearError,
    }),
    [
      clearError,
      connectDeezer,
      connectSpotify,
      connectWithCallback,
      connectWithReturnPage,
      disconnect,
      error,
      isBusy,
      loadArtists,
      selectArtist,
      sync,
    ]
  );
}
